import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

// ── The employee's acknowledgement page ────────────────────────────────────
// Opened from a one-time link in an email. No login, no portal chrome, nothing
// of the portal visible but this one document.
//
// It uses a bare axios instance, NOT the shared api client: that one attaches
// whatever token happens to be in localStorage and redirects to the login page
// on a 401. Here there is no session, and an employee must never be bounced to
// a sign-in screen.
const http = axios.create({ baseURL: '/api/ack' });

const PAGE = {
  minHeight: '100vh', background: 'var(--bg)', padding: '24px 16px',
  fontFamily: "'DM Sans', system-ui, sans-serif", color: 'var(--text)',
};
const SHEET = {
  maxWidth: 820, margin: '0 auto', background: 'var(--bg2)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
  padding: '28px 30px',
};

// ── Signature pad ───────────────────────────────────────────────────────────
// Draw with a mouse on a desktop, a finger on a phone. Pointer events cover
// both, so there is no separate touch path to get wrong.
//
// The canvas is sized to its own box times the device pixel ratio, otherwise
// the stroke looks soft on a phone; the drawing coordinates stay in CSS pixels
// because the context is scaled once.
//
// Optional by design: typing the name is the signature that counts, and an
// older colleague on a small screen should not be blocked by a drawing box.
function SignaturePad({ onChange, disabled }) {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [has, setHas] = useState(false);

  // onChange lives in a ref so `reset` never changes identity. Depending on
  // the prop directly rebuilt reset on every render, which re-ran the effect
  // below and cleared the canvas — the box was there and the ink disappeared
  // the instant the first stroke set state.
  const cb = useRef(onChange);
  useEffect(() => { cb.current = onChange; }, [onChange]);

  const reset = useCallback(() => {
    const c = canvas.current, box = wrap.current;
    if (!c || !box) return;
    const ratio = window.devicePixelRatio || 1;
    const w = box.clientWidth, h = 150;
    c.width = w * ratio; c.height = h * ratio;
    c.style.width = `${w}px`; c.style.height = `${h}px`;
    const ctx = c.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, w, h);
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#16203c';
    dirty.current = false; setHas(false); cb.current(null);
  }, []);

  // Size and clear ONCE on mount. Re-running this after a stroke is what
  // erased the signature.
  useEffect(() => { reset(); }, [reset]);
  // A phone rotated from portrait to landscape changes the box width; without
  // this the canvas keeps the old size and the stroke lands off the pointer.
  useEffect(() => {
    const onResize = () => { if (!dirty.current) reset(); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [reset]);

  const at = e => {
    const r = canvas.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = e => {
    if (disabled) return;
    e.preventDefault();
    drawing.current = true;
    canvas.current.setPointerCapture?.(e.pointerId);
    const { x, y } = at(e);
    const ctx = canvas.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.01, y);  // a tap leaves a dot
    ctx.stroke();
    dirty.current = true; setHas(true);
  };
  const move = e => {
    if (!drawing.current || disabled) return;
    e.preventDefault();
    const { x, y } = at(e);
    const ctx = canvas.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (dirty.current) cb.current(canvas.current.toDataURL('image/png'));
  };

  return (
    <div ref={wrap}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                    gap: 10, marginBottom: 5 }}>
        <label style={{ fontSize: 13, color: 'var(--text2)' }}>
          Sign here (optional)
        </label>
        {has && !disabled && (
          <button type="button" onClick={reset}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                     fontSize: 12.5, fontWeight: 600, color: 'var(--accent)' }}>Clear</button>
        )}
      </div>
      <canvas ref={canvas}
        onPointerDown={start} onPointerMove={move}
        onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
        style={{ display: 'block', width: '100%', height: 150, background: '#fff',
                 border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                 touchAction: 'none', cursor: disabled ? 'default' : 'crosshair' }} />
      <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 5 }}>
        Draw with your mouse, or your finger on a phone. You can leave this blank —
        your typed name below is your signature either way.
      </div>
    </div>
  );
}

export { SignaturePad };

// ── The HR / employer representative's page ────────────────────────────────
// Opened from a one-time link, like the employee's. HR has no portal account,
// so this is the whole of their involvement: see what the employee signed, and
// sign it. Nothing on this page can change a detail of the agreement.
export function AckHrSign({ token }) {
  const [d, setD] = useState(null);
  const [gone, setGone] = useState(null);
  const [f, setF] = useState({ typed_name: '', signature_image: null, agree: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    http.get(`/hr/${token}`).then(r => setD(r.data))
      .catch(e => setGone(e.response?.data?.error || 'invalid'));
  }, [token]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const sign = async () => {
    setErr(''); setBusy(true);
    try { setDone((await http.post(`/hr/${token}/sign`, f)).data); }
    catch (e) { setErr(e.response?.data?.error || 'Could not submit. Please try again.'); }
    finally { setBusy(false); }
  };

  if (gone) {
    const [title, body] = gone === 'used'
      ? ['This agreement is already signed', 'Nothing further is needed from you.']
      : (GONE[gone] || GONE.invalid);
    return (
      <div style={PAGE}>
        <div style={{ ...SHEET, textAlign: 'center', maxWidth: 520 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
          <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8 }}>{title}</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text2)' }}>{body}</p>
        </div>
      </div>
    );
  }

  if (done) return (
    <div style={PAGE}>
      <div style={{ ...SHEET, textAlign: 'center', maxWidth: 520 }}>
        <div style={{ fontSize: 34, marginBottom: 8 }}>✓</div>
        <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8, color: 'var(--green)' }}>
          Thank you — signature recorded
        </h1>
        <p style={{ fontSize: 13.5, color: 'var(--text2)' }}>
          {done.ack_code}, signed as <b>{done.name}</b>. The IT department holds the
          completed agreement.
        </p>
      </div>
    </div>
  );

  if (!d) return <div style={{ ...PAGE, color: 'var(--text2)' }}>Loading…</div>;
  const doc = d.document;
  const emp = d.employee_signature || {};

  return (
    <div style={PAGE}>
      <div style={SHEET}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={{ fontSize: 19, fontWeight: 700, color: 'var(--accent)' }}>{doc.template.title}</h1>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
            {d.ack_code} · for the employer representative's signature
          </div>
        </div>

        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', padding: 16, marginBottom: 18,
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px,1fr))',
                      gap: 14 }}>
          <Field label="Employee" value={doc.employee.name} />
          <Field label="Employee ID" value={doc.employee.employee_id} />
          <Field label="Designation / Campus"
            value={[doc.employee.designation, doc.employee.campus].filter(Boolean).join(' / ')} />
          <Field label="Item" value={doc.asset.make_model} />
          <Field label="Serial / Tag"
            value={[doc.asset.serial, doc.asset.tag].filter(Boolean).join(' / ')} />
          <Field label="Issued on" value={doc.asset.issue_date} />
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 8 }}>
          Signed by the employee
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                      padding: '12px 14px', marginBottom: 18 }}>
          {emp.image && (
            <img src={emp.image} alt="Employee signature"
              style={{ height: 52, background: '#fff', display: 'block', marginBottom: 6 }} />
          )}
          <div style={{ fontSize: 13 }}>
            <b>{emp.name}</b>
            <span style={{ color: 'var(--text2)' }}> · {emp.at}</span>
          </div>
          {emp.remarks && emp.remarks !== 'Nil' && (
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
              Remarks: {emp.remarks}
            </div>
          )}
        </div>

        <details style={{ marginBottom: 18 }}>
          <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            color: 'var(--accent)' }}>
            Read the agreement ({(doc.template.clauses || []).length} clauses)
          </summary>
          <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)', padding: '12px 14px', marginTop: 8 }}>
            {(doc.template.clauses || []).map((c, i) => (
              <p key={i} style={{ fontSize: 12.5, marginBottom: 9, textAlign: 'justify' }}>
                <b>{i + 1}. {c.heading}.</b>{' '}
                <span style={{ color: 'var(--text2)' }}>{c.body}</span>
              </p>
            ))}
          </div>
        </details>

        <div style={{ marginBottom: 16 }}>
          <SignaturePad onChange={img => set('signature_image', img)} />
        </div>

        <div className="form-group">
          <label>Type your name</label>
          <input value={f.typed_name} onChange={e => set('typed_name', e.target.value)}
            placeholder="Name and designation, e.g. Sakthivel Praveen, HR Executive" />
        </div>

        <label className="opt" style={{ display: 'flex', gap: 9, alignItems: 'flex-start',
                                        margin: '6px 0 16px', fontSize: 13 }}>
          <input type="checkbox" checked={f.agree}
            onChange={e => set('agree', e.target.checked)} style={{ marginTop: 3 }} />
          <span>I am authorised to sign this agreement on behalf of the employer.</span>
        </label>

        {err && <div className="err-msg" style={{ marginBottom: 12 }}>{err}</div>}

        <button className="btn btn-primary" disabled={busy || !f.agree || !f.typed_name.trim()}
          onClick={sign} style={{ minHeight: 44 }}>
          {busy ? 'Submitting…' : 'Sign'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 600 }}>{value || '—'}</div>
    </div>
  );
}

const GONE = {
  invalid:   ['This link is not valid', 'It may have been typed incorrectly, or replaced by a newer link. Please ask the IT department to send it again.'],
  used:      ['This acknowledgement is already signed', 'The link works once only. Your signed copy is with the IT department, and you can see it in the portal under My Assets.'],
  expired:   ['This link has expired', 'Please ask the IT department to send you a new one.'],
  cancelled: ['This acknowledgement was cancelled', 'Please contact the IT department if you still hold the item.'],
};

export default function AckSign({ token, preview = false, previewData = null }) {
  const [d, setD] = useState(previewData);
  const [gone, setGone] = useState(null);
  const [f, setF] = useState({ typed_name: '', employee_id: '', phone: '',
                               place: '', remarks: '', agree: false,
                               signature_image: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (preview) return;                       // preview never touches the link
    http.get(`/${token}`)
      .then(r => {
        setD(r.data);
        const c = r.data.confirm || {};
        setF(s => ({ ...s, typed_name: c.name || '', employee_id: c.employee_id || '',
                     phone: c.phone || '', place: c.campus || '' }));
      })
      .catch(e => setGone(e.response?.data?.error || 'invalid'));
  }, [token, preview]);

  useEffect(() => {
    if (!preview || !previewData) return;
    const c = previewData.confirm || {};
    setF(s => ({ ...s, typed_name: c.name || '', employee_id: c.employee_id || '',
                 phone: c.phone || '', place: c.campus || '' }));
  }, [preview, previewData]);

  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const sign = async () => {
    setErr(''); setBusy(true);
    try {
      const r = await http.post(`/${token}/sign`, f);
      setDone(r.data);
    } catch (e) {
      setErr(e.response?.data?.error || 'Could not submit. Please try again.');
    } finally { setBusy(false); }
  };

  if (gone) {
    const [title, body] = GONE[gone] || GONE.invalid;
    return (
      <div style={PAGE}>
        <div style={{ ...SHEET, textAlign: 'center', maxWidth: 520 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🔒</div>
          <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8 }}>{title}</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text2)' }}>{body}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={PAGE}>
        <div style={{ ...SHEET, textAlign: 'center', maxWidth: 560 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>✓</div>
          <h1 style={{ fontSize: 19, fontWeight: 700, marginBottom: 8, color: 'var(--green)' }}>
            Thank you — acknowledgement recorded
          </h1>
          <p style={{ fontSize: 13.5, color: 'var(--text2)', marginBottom: 18 }}>
            Reference <b>{done.ack_code}</b>, signed as <b>{done.name}</b>.
          </p>
          <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius)', padding: '14px 16px',
                        fontSize: 13.5, textAlign: 'left' }}>
            {done.emailed_to
              ? <>A signed copy is on its way to <b>{done.emailed_to}</b> for your records.
                  It may take a minute to arrive.</>
              : <>A copy has been kept with the IT department.</>}
          </div>
          <p style={{ fontSize: 12, color: 'var(--text2)', marginTop: 16 }}>
            This link will not open again. You can close this page.
          </p>
        </div>
      </div>
    );
  }

  if (!d) return <div style={{ ...PAGE, color: 'var(--text2)' }}>Loading…</div>;

  const doc = d.document;
  const c = d.confirm || {};

  return (
    <div style={PAGE}>
      {preview && (
        <div style={{ maxWidth: 820, margin: '0 auto 12px', padding: '10px 14px',
                      background: '#FDF3D7', border: '1px solid var(--amber)',
                      borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600,
                      color: '#7A4E06' }}>
          PREVIEW MODE — this is exactly what the employee will see. Nothing can be
          signed or submitted here, and the one-time link is untouched.
        </div>
      )}

      <div style={SHEET}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <h1 style={{ fontSize: 19, fontWeight: 700, color: 'var(--accent)' }}>{doc.template.title}</h1>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
            Reference: {doc.reference} &middot; {d.ack_code}
          </div>
        </div>

        <p style={{ fontSize: 13.5, marginBottom: 18 }}>{doc.template.intro}</p>

        {/* What is being issued */}
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', padding: 16, marginBottom: 20,
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px,1fr))',
                      gap: 14 }}>
          <Field label="Item" value={doc.asset.make_model} />
          <Field label="Serial number" value={doc.asset.serial} />
          <Field label="Asset tag" value={doc.asset.tag} />
          <Field label="Charger / accessories" value={doc.asset.accessories} />
          <Field label="Issued on" value={doc.asset.issue_date} />
          <Field label="Condition" value={doc.asset.condition} />
        </div>

        {/* Your details — the one moment we can reliably collect these */}
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 10 }}>
          Please check your details
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))',
                      gap: 14, marginBottom: 20 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Name</label>
            <input value={c.name} disabled />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Employee ID</label>
            <input value={f.employee_id} disabled={preview}
              onChange={e => set('employee_id', e.target.value)}
              placeholder="e.g. TIPS-AD19-4856" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Contact number</label>
            <input value={f.phone} disabled={preview}
              onChange={e => set('phone', e.target.value)} placeholder="10-digit mobile" />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Official email</label>
            <input value={c.email} disabled />
          </div>
        </div>

        {/* The agreement */}
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: '.05em', color: 'var(--text2)', marginBottom: 10 }}>
          The agreement
        </div>
        <div style={{ maxHeight: 340, overflowY: 'auto', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 18,
                      background: 'var(--bg2)' }}>
          {(doc.template.clauses || []).map((cl, i) => (
            <p key={i} style={{ fontSize: 13, marginBottom: 10, textAlign: 'justify' }}>
              <b>{i + 1}. {cl.heading}.</b>{' '}
              <span style={{ color: 'var(--text2)' }}>{cl.body}</span>
            </p>
          ))}
        </div>

        <p style={{ fontSize: 13.5, marginBottom: 14 }}>{doc.template.declaration}</p>

        <div className="form-group">
          <label>Remarks / pre-existing condition (optional)</label>
          <input value={f.remarks} disabled={preview}
            onChange={e => set('remarks', e.target.value)}
            placeholder="Anything you noticed about the item — a scratch, a missing part" />
        </div>

        <div style={{ marginBottom: 16 }}>
          <SignaturePad disabled={preview}
            onChange={img => set('signature_image', img)} />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))',
                      gap: 14 }}>
          <div className="form-group">
            <label>Type your full name as your signature</label>
            <input value={f.typed_name} disabled={preview}
              onChange={e => set('typed_name', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Place</label>
            <input value={f.place} disabled={preview} onChange={e => set('place', e.target.value)} />
          </div>
        </div>

        <label className="opt" style={{ display: 'flex', gap: 9, alignItems: 'flex-start',
                                        margin: '6px 0 16px', fontSize: 13 }}>
          <input type="checkbox" checked={f.agree} disabled={preview}
            onChange={e => set('agree', e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            I confirm the details above are correct, I have read and understood the
            agreement, and I accept custody of the item listed.
          </span>
        </label>

        {err && <div className="err-msg" style={{ marginBottom: 12 }}>{err}</div>}

        <button className="btn btn-primary" disabled={preview || busy || !f.agree}
          onClick={sign} style={{ minHeight: 44 }}>
          {preview ? 'Signing is disabled in preview' : busy ? 'Submitting…' : 'Sign and submit'}
        </button>

        <p style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 14 }}>
          {doc.template.signature_note}
        </p>
      </div>
    </div>
  );
}
